import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FlexRow } from '@app/flex-framework/flex-api.model';
import { FlexGridService } from '@app/flex-framework/services/flex-grid.service';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { LinkRowLink } from '@app/shared/components/link-row/link-row.model';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { Resource } from '@app/target/target-api.model';
import { TranslateService } from '@ngx-translate/core';
import SwiperCore, { A11y } from 'swiper';

SwiperCore.use([A11y]);

@Component({
  selector: 'dgx-carousel-content',
  templateUrl: './carousel-content.component.html',
  styleUrls: ['./carousel-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselContentComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() public customSlidesPerView: number;
  @Input() public row: FlexRow;
  @Input() public label: string;
  @Input() public menuConfig: MenuViewModel[];
  @Input() public links: LinkRowLink[];
  @Input() public markdownDescription: string;
  @Input() public loadingSuggestions: boolean;
  @Input() public permanentEditMode: boolean;
  @Input() public hideReorderButton: boolean = true;
  @Input() public hideQuickEditHeader: boolean = true;

  @Output() public addResource: EventEmitter<any> = new EventEmitter<any>();
  @Output() public removeSuggestion = new EventEmitter<{
    row: FlexRow;
    item: Resource;
  }>();

  @ViewChild('swiper') public swiper;

  public tileType: string;
  public slidesPerView: number;
  public isEndOfCarousel: boolean = true;
  public isBeginningOfCarousel: boolean = true;
  public isSliderInitialized: boolean = false;
  public isEditing: boolean = false;
  public readonly WINDOW_RESIZE_TIMEOUT: number = 450;
  private windowResizeTimer;

  constructor(
    private flexRowService: FlexRowService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private flexGridService: FlexGridService
  ) {}

  public ngAfterViewInit(): void {
    this.updateCarousel();
  }

  public ngOnInit(): void {
    // No need to do any subscribe logic if we're in PEM.
    if (this.permanentEditMode) {
      this.isEditing = true;
      this.setCarouselSlides();
      this.cdr.markForCheck();
      return;
    }

    this.activatedRoute.queryParams?.subscribe((params) => {
      this.isEditing = params.editMode === 'true';
      this.setCarouselSlides();
      this.cdr.markForCheck();
    });
  }

  public ngOnChanges({ row }: SimpleChanges): void {
    // otherwise, we only care about updates to the row
    if (row) {
      setTimeout(() => {
        // update the swiperRef in a timeout so the DOM has a chance to update with
        // the new targetResources.
        this.swiper?.swiperRef?.update();
        if (this.isSliderInitialized) {
          this.updateCarousel();
          this.setCarouselSlides();
        }
      });
    }
  }

  @HostListener('window:resize', ['$event'])
  public onWindowResize() {
    clearTimeout(this.windowResizeTimer);
    this.windowResizeTimer = setTimeout(() => {
      const newSlidesPerView = this.setSlidesPerView();
      if (this.slidesPerView !== newSlidesPerView) {
        this.slidesPerView = newSlidesPerView;
        this.cdr.detectChanges();
      }
    }, this.WINDOW_RESIZE_TIMEOUT);
  }

  public removeResourceFromRow(row, item): void {
    const id = item.referenceId || item.providerId;
    this.flexRowService.removeResourceFromRow(row, id);
  }

  /*
   * menu config function for the provider bubble
   */
  public providerBubbleMenuConfigFn(row): (provider: any) => MenuViewModel[] {
    return (provider) => [
      {
        title: this.translateService.instant('dgTagRating_RemoveFromPlan'),
        isHidden: () => !this.isEditing,
        defaultAction: () => this.removeResourceFromRow(row, provider),
      },
    ];
  }

  /*
   * sets carousel attributes based on the type
   * of content being displayed in the carousel
   */
  public setCarouselSlides(): void {
    this.tileType =
      this.row.section.resourceType === 'Input'
        ? 'UserContent'
        : this.row.section.resourceType;
    this.slidesPerView = this.setSlidesPerView();
  }

  /*
   * sets the number of slides to show at once in the carousel
   * based on the type of content in the carousel
   */
  public setSlidesPerView(): number {
    if (!this.tileType) {
      return undefined;
    } else {
      return this.flexGridService.getRowSize(this.row.gridClass);
    }
  }

  /*
   * updates the carousel to set aria-hidden attribute when slides are not visible (for the
   * screen reader), whether the carousel is at the end or beginning (for carousel navigation),
   * and whether the slider has been initialized
   */
  public updateCarousel(): void {
    // unhide all slides (resets the attributes)
    const slides = this.swiper?.swiperRef?.$el?.find('.swiper-slide') || [];
    slides.forEach((slide) => {
      slide.setAttribute('aria-hidden', false);
    });

    // hide slides that aren't visible
    const hiddenSlides =
      this.swiper?.swiperRef?.$el?.find(
        '.swiper-slide:not(.swiper-slide-visible)'
      ) || [];
    hiddenSlides.forEach((slide) => {
      slide.setAttribute('aria-hidden', true);
    });

    // Ensure that all slides within 'any' carousels have non-null resourceType properties
    if (this.tileType === 'Any') {
      this.row.targetResources = this.row.targetResources.map((item) => ({
        ...item,
        reference: {
          ...item.reference,
          resourceType:
            item.reference.resourceType ??
            item.resourceType ??
            item.referenceType,
        },
      }));
    }

    this.isEndOfCarousel = this.swiper.swiperRef.isEnd;
    this.isBeginningOfCarousel = this.swiper.swiperRef.isBeginning;

    this.isSliderInitialized = true;
  }

  public trackByReferenceId(_index, item): number {
    return item.referenceId;
  }

  public trackByStubIndex(index): number {
    return index;
  }

  public addTargetResource(event): void {
    this.addResource.emit(event);
  }

  public removeAutoSuggestion(event, row, item): void {
    this.removeSuggestion.emit({ row, item });
  }

  /*
   * swipes the slides in the carousel
   */
  public swipe(toPrevious?: boolean): void {
    if (toPrevious) {
      this.swiper.swiperRef.slidePrev();
    } else {
      this.swiper.swiperRef.slideNext();
    }
  }
}
